import { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Link } from "@corpuscomplete/web/components/link";
import { Recipe } from "@corpuscomplete/core/nutrition/recipe";
import { RecipeVariation } from "@corpuscomplete/core/nutrition/recipe-variation";
import { RecipeDetail } from "@corpuscomplete/core-ui/nutrition/recipe/detail";
import { FoodItem } from "@corpuscomplete/web/components/nutrition/food-item";
import { NutrientAmountsLabel } from "@corpuscomplete/web/components/nutrition/nutrients-label";
import { NotFoundPage } from "@corpuscomplete/web/components/not-found-page";
import { Badge } from "@corpuscomplete/web-ui/components/badge";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Folder, PencilIcon } from "lucide-react";
import { RecipeAuthoringPageShell } from "../components/recipe-authoring-page-shell";
import {
  RecipeMetric,
  RecipePageSection,
  RecipeStepNumber,
} from "../components/recipe-page-section";
import { Scope } from "@corpuscomplete/core/common/scope";
import { ScopeBreadcrumb } from "@corpuscomplete/web/modules/common/components/scope-breadcrumb";
import { useAuth } from "@corpuscomplete/web/auth/hooks";
import { FoodCollectionUI } from "@corpuscomplete/core-ui/nutrition/food-collection";

/**
 * Recipe detail route boundary for a single recipe id.
 * Contract: hands route param id to RecipeDetail provider and exposes
 * read/edit UI while domain edit constraints stay in core-ui hooks.
 */
export function RecipeDetailPage({ id, variationId }: { id: string; variationId?: string }) {
  return (
    <RecipeDetail.DataProvider recipeId={id}>
      <RecipeDetailContent recipeId={id} variationId={variationId} />
    </RecipeDetail.DataProvider>
  );
}

function RecipeDetailContent({
  recipeId,
  variationId,
}: {
  recipeId: string;
  variationId?: string;
}) {
  type DisplayRecipe = Pick<
    Recipe.Info,
    | "id"
    | "version"
    | "base"
    | "meta"
    | "description"
    | "prepTimeMinutes"
    | "cookTimeMinutes"
    | "serving"
    | "ingredients"
    | "steps"
  >;

  const navigate = useNavigate();
  const { userId } = useAuth();
  const { constraints, isLoading, recipe: baseRecipe } = RecipeDetail.useDetails();
  const { canEdit } = constraints;
  const collections = FoodCollectionUI.useForContents();

  const variationPatchResult = useMemo(() => {
    if (!baseRecipe || !variationId) {
      return null;
    }

    const variation = baseRecipe.variations.find((v) => v.id === variationId);
    return variation ? RecipeVariation.applyPatch(baseRecipe, variation, variation) : null;
  }, [baseRecipe, variationId]);

  const recipe: DisplayRecipe | undefined = variationPatchResult?.value ?? baseRecipe;
  const selectedVariation = variationId
    ? baseRecipe?.variations.find((variation) => variation.id === variationId)
    : undefined;
  const selectedCollection = baseRecipe?.meta.collectionId
    ? collections.find((collection) => collection.id === baseRecipe.meta.collectionId)
    : undefined;
  const scope = selectedCollection
    ? Scope.fromId({ scopeId: selectedCollection.scopeId, userId })
    : null;
  const recipesCrumb = scope
    ? {
        label: "Recipes",
        renderLink:
          scope.kind === "personal" ? (
            <Link to="/personal/recipes" />
          ) : (
            <Link to="/spaces/$spaceId/recipes" params={{ spaceId: scope.spaceId }} />
          ),
      }
    : { loading: true as const };

  if (isLoading) {
    return (
      <RecipeAuthoringPageShell
        title="Recipe"
        breadcrumb={
          <ScopeBreadcrumb scope={null} scopeBehavior="static" crumbs={[{ loading: true }]} />
        }
      >
        <Text variant="sm" className="text-muted-foreground">
          Loading...
        </Text>
      </RecipeAuthoringPageShell>
    );
  }

  if (!recipe?.base || !baseRecipe) {
    return <NotFoundPage />;
  }

  const setSelectedVariation = (nextVariationId: string | null) => {
    void navigate({
      to: "/recipes/$id",
      params: { id: recipe.id },
      search: nextVariationId ? { variationId: nextVariationId } : undefined,
      replace: true,
    });
  };

  return (
    <RecipeAuthoringPageShell
      breadcrumb={
        <ScopeBreadcrumb
          scope={scope}
          scopeBehavior="static"
          crumbs={[recipesCrumb, { label: recipe.base.name || "Recipe", current: true }]}
        />
      }
      title={recipe.base.name}
      status={
        <div className="flex flex-wrap items-center gap-2">
          {recipe.meta.isArchived ? (
            <Badge>
              <Text variant="sm">Archived</Text>
            </Badge>
          ) : null}
          {selectedVariation?.base?.name ? (
            <Badge variant="outline">
              <Text variant="sm">Variation: {selectedVariation.base.name}</Text>
            </Badge>
          ) : null}
        </div>
      }
      headerActions={
        canEdit ? (
          <Button
            variant="outline"
            size="sm"
            render={<Link to="/recipes/$id/edit" params={{ id: recipeId }} />}
          >
            <PencilIcon className="mr-2 size-4" />
            Edit
          </Button>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          {recipe.description ? (
            <Text variant="md" className="max-w-3xl leading-6 text-muted-foreground">
              {recipe.description}
            </Text>
          ) : null}

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            {selectedCollection ? (
              <div className="flex flex-col gap-1">
                <Text variant="xs" className="text-muted-foreground">
                  Collection
                </Text>
                <div className="flex items-center gap-2">
                  <Folder className="size-4 text-muted-foreground" />
                  <Text variant="sm" className="font-medium">
                    {selectedCollection.name}
                  </Text>
                </div>
              </div>
            ) : null}
            {recipe.prepTimeMinutes !== null ? (
              <RecipeMetric label="Prep Time" value={`${recipe.prepTimeMinutes} mins`} />
            ) : null}
            {recipe.cookTimeMinutes !== null ? (
              <RecipeMetric label="Cook Time" value={`${recipe.cookTimeMinutes} mins`} />
            ) : null}
            <RecipeMetric
              label="Variations"
              value={
                baseRecipe.variations.length === 0
                  ? "Base recipe only"
                  : `${baseRecipe.variations.length + 1} versions total`
              }
            />
            <RecipeMetric
              label="Yield"
              value={`${recipe.serving.perRecipe} ${recipe.serving.name}`}
            />
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.7fr)_minmax(18rem,1fr)]">
          <RecipePageSection
            title="Ingredients"
            description="Everything that goes into this recipe."
          >
            <div className="flex flex-col gap-1">
              {recipe.ingredients.length === 0 ? (
                <Text variant="sm" className="text-muted-foreground">
                  No ingredients yet.
                </Text>
              ) : (
                recipe.ingredients.map((ingredient: Recipe.Info["ingredients"][number]) => (
                  <FoodItem key={ingredient.id} item={ingredient} />
                ))
              )}
            </div>
          </RecipePageSection>

          <div className="flex flex-col gap-6">
            <RecipePageSection title="Yield" description="How much the full recipe makes.">
              <Text variant="sm" className="font-medium">
                {recipe.serving.perRecipe} {recipe.serving.name}
              </Text>
            </RecipePageSection>

            <RecipePageSection
              title="Nutrition Per Serving"
              description="Calculated per-serving nutrients from the current recipe ingredients."
            >
              <NutrientAmountsLabel amounts={recipe.serving.nutrients} />
            </RecipePageSection>
          </div>
        </div>

        <RecipePageSection title="Instructions" description="Follow the steps in order.">
          {recipe.steps.length === 0 ? (
            <Text variant="sm" className="text-muted-foreground">
              No steps provided.
            </Text>
          ) : (
            <div className="flex flex-col gap-3">
              {recipe.steps.map((step: Recipe.Info["steps"][number], index: number) => (
                <div key={step.id} className="flex gap-3">
                  <RecipeStepNumber>{index + 1}</RecipeStepNumber>
                  <Text variant="sm" className="pt-1 leading-6">
                    {step.text}
                  </Text>
                </div>
              ))}
            </div>
          )}
        </RecipePageSection>

        <RecipePageSection
          title="Curated Variations"
          description="Switch between the base recipe and curated alternatives."
        >
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={!variationId ? "default" : "outline"}
              onClick={() => setSelectedVariation(null)}
            >
              Base
            </Button>
            {baseRecipe.variations.map((variation) => (
              <Button
                key={variation.id}
                size="sm"
                variant={variationId === variation.id ? "default" : "outline"}
                onClick={() => setSelectedVariation(variation.id)}
              >
                {variation.base?.name ?? ""}
              </Button>
            ))}
          </div>
        </RecipePageSection>
      </div>
    </RecipeAuthoringPageShell>
  );
}
